import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, ExternalLink } from "lucide-react";
import api from "@/api";
import { MILESTONE_ICONS, fmtDate } from "@/components/PlayerPanels";
import { useI18n } from "@/i18n";
import MediaImage from "@/components/MediaImage";
import { useSeo } from "@/seo";
import { milestoneLabel } from "@/milestones";

export default function CardPage() {
  const { slug: id } = useParams();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [links, setLinks] = useState([]);
  const [error, setError] = useState(null);

  // The card pages are the long tail of the sitemap (2 884 URLs): without this the
  // prerenderer captured a bare "Rookie Radar" title on every one of them.
  const seoCard = data?.card;
  const seoName = data?.player?.name || seoCard?.player_name_raw;
  useSeo(
    seoCard ? [seoName, seoCard.set_name, seoCard.card_number ? `#${seoCard.card_number}` : null].filter(Boolean).join(" — ") : null,
    seoCard ? t("cards.seo_card_desc", {
      name: seoName || "",
      set: [seoCard.set_name, seoCard.season].filter(Boolean).join(" "),
      number: seoCard.card_number || "",
    }) : null,
    seoCard ? `/cards/${id}` : null,
  );

  useEffect(() => {
    api.get(`/cards/${id}`).then((r) => setData(r.data)).catch((e) => setError(e.message));
    api.get(`/cards/marketplace-links?lang=${lang}`).then((r) => setLinks(r.data.links || [])).catch(() => setLinks([]));
  }, [id, lang]);

  if (error) return <p className="pt-16 text-center text-sm text-red-400" data-testid="card-error">{error}</p>;
  if (!data) return <p className="pt-16 text-center font-mono2 text-sm text-slate-500">{t("common.loading")}</p>;

  const { card, player } = data;
  // Public player → his canonical name is the title and the link that keeps the page
  // alive. Non-public / unknown player → the printed name IS the page.
  const printed = card.player_name_raw;
  const title = player?.name || printed;
  const showPrinted = printed && printed !== title;
  // Query spec: player name + set name + season + "rookie", URL-encoded.
  const q = encodeURIComponent(
    [title, card.set_name, card.season, "rookie"].filter(Boolean).join(" ")
  );

  return (
    <div className="pt-6" data-testid="card-page">
      {player && (
        <Link to={`/players/${player.player_id}`} data-testid="card-back-to-player" className="mb-4 inline-flex items-center gap-1.5 font-mono2 text-xs uppercase tracking-wider text-slate-400 hover:text-[#00FF87]">
          <ArrowLeft className="h-3.5 w-3.5" /> {player.name}
        </Link>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <div className="hud-card rounded-xl p-5" data-testid="card-details">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {player ? (
                  <Link to={`/players/${player.player_id}`} data-testid="card-title-link">
                    <h1 className="font-display text-3xl font-black uppercase leading-none tracking-tight text-white transition-colors hover:text-[#00FF87]" data-testid="card-title">{title}</h1>
                  </Link>
                ) : (
                  <h1 className="font-display text-3xl font-black uppercase leading-none tracking-tight text-white" data-testid="card-title">{title}</h1>
                )}
                <p className="mt-1 font-mono2 text-xs uppercase tracking-wider text-slate-400" data-testid="card-set-line">
                  {card.set_name}{card.season && !String(card.set_name).includes(card.season) ? ` ${card.season}` : ""}
                </p>
                {showPrinted && (
                  <p className="mt-0.5 font-mono2 text-[11px] text-slate-500" data-testid="card-printed-name">
                    {t("cards.printed_label")} : {printed}
                  </p>
                )}
                {/* Nom d'état civil : visible ICI seulement. Une carte le porte souvent,
                    et le collectionneur doit pouvoir faire le lien avec le joueur. */}
                {player?.full_name && player.full_name !== title && player.full_name !== printed && (
                  <p className="mt-0.5 font-mono2 text-[11px] text-slate-500" data-testid="card-player-full-name">
                    {t("cards.full_name_label")} : {player.full_name}
                  </p>
                )}
              </div>
              {card.match_confidence === "reviewed" && (
                <span className="flex shrink-0 items-center gap-1 rounded-full border border-[#00FF87]/50 bg-[#00FF87]/10 px-2 py-0.5 font-mono2 text-[10px] font-bold uppercase tracking-wider text-[#00FF87]" data-testid="card-reviewed-badge">
                  <BadgeCheck className="h-3 w-3" /> {t("cards.reviewed_badge")}
                </span>
              )}
            </div>
            <p className="mt-1 font-mono2 text-xs uppercase tracking-wider text-slate-500">{card.manufacturer}</p>
            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div><dt className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("cards.player")}</dt><dd className="text-slate-200">{printed}</dd></div>
              <div><dt className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("cards.parallel")}</dt><dd className="text-slate-200">{card.parallel}</dd></div>
              {card.subset && card.subset !== "Base" && (
                <div data-testid="card-subset-row"><dt className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("cards.subset")}</dt><dd className="text-slate-200">{card.subset}</dd></div>
              )}
              <div><dt className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("cards.number")}</dt><dd className="text-slate-200">{card.card_number || "—"}</dd></div>
              <div><dt className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("cards.print_run")}</dt><dd className="text-slate-200">{card.print_run || "—"}</dd></div>
            </dl>
            {card.is_rookie && <span className="mt-4 inline-block rounded-full border border-[#FFD600]/50 bg-[#FFD600]/10 px-2.5 py-1 font-mono2 text-[10px] font-bold uppercase tracking-wider text-[#FFD600]">RC</span>}
          </div>

          {!player && (
            <div className="hud-card rounded-xl p-4" data-testid="card-unlinked-block">
              <p className="text-xs text-slate-400" data-testid="card-no-senior-apps">{t("cards.no_senior_apps_alert")}</p>
              <div className="mt-3 rounded-lg border border-[#1F293D] bg-[#07090E]/60 p-3" data-testid="card-no-trigger">
                <Link to="/auth?mode=register" className="font-mono2 text-[11px] font-bold uppercase tracking-wider text-[#00FF87] hover:underline" data-testid="card-alert-signup">{t("cards.alert_signup")}</Link>
              </div>
            </div>
          )}

          {player && (
            <div className="hud-card rounded-xl p-4" data-testid="card-player-block">
              <div className="flex items-center gap-3">
                <MediaImage
                  src={player.club_logo}
                  alt={t("media.logo_alt", { club: player.club_name || "—" })}
                  kind="club" size={48} surface="player_page"
                  imgClassName="object-contain p-1.5"
                  testid="card-player-media"
                />
                <div>
                  {player.watching ? (
                    <Link to={`/players/${player.player_id}`} className="font-display text-xl font-semibold uppercase text-slate-50 hover:text-[#00FF87]" data-testid="card-player-link">{player.name}</Link>
                  ) : (
                    <p className="font-display text-xl font-semibold uppercase text-slate-50" data-testid="card-player-name">{player.name}</p>
                  )}
                  <p className="text-xs text-slate-400">{player.club_name} · {player.league_name}</p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 font-mono2 text-[11px] text-slate-400" data-testid="card-player-sporting-state">
                <span>{t("cards.apps")}: <b className="text-slate-200">{player.appearances ?? "—"}</b></span>
                <span>{t("cards.minutes")}: <b className="text-slate-200">{player.minutes ?? "—"}</b></span>
              </div>
              {!player.appearances && !(player.milestones || []).length && (
                <p className="mt-2 text-xs text-slate-500" data-testid="card-no-senior-apps">{t("cards.no_senior_apps")}</p>
              )}
              {(player.milestones || []).length > 0 && (
                <ul className="mt-3 flex flex-wrap gap-2" data-testid="card-player-milestones">
                  {player.milestones.map((m, i) => {
                    const Icon = MILESTONE_ICONS[m.type] || BadgeCheck;
                    return (
                      <li
                        key={`${m.type}-${m.date}-${i}`}
                        data-testid={`card-milestone-${m.type}`}
                        className="flex items-center gap-2 rounded-full border border-[#00FF87]/40 bg-[#00FF87]/10 px-2.5 py-1"
                      >
                        <Icon className="h-3 w-3 shrink-0 text-[#00FF87]" />
                        <span className="font-mono2 text-[10px] font-bold uppercase tracking-wider text-[#00FF87]">
                          {milestoneLabel(m, t)}
                        </span>
                        <span className="font-mono2 text-[10px] text-slate-400">{fmtDate(m.date, lang)}</span>
                        <span
                          className={`font-mono2 text-[9px] uppercase tracking-wider ${m.confidence === "confirmed" ? "text-[#00FF87]/70" : m.confidence === "pending" ? "text-slate-500" : "text-[#FFD600]/80"}`}
                          data-testid={`card-milestone-confidence-${m.type}`}
                        >
                          {t(`milestone.${m.confidence || "unconfirmed"}`)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
              {player.watching && player.signal ? (
                <div className="mt-3 flex items-center gap-2" data-testid="card-player-signal">
                  <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("cards.signal")}</span>
                  <span className="rounded-full border border-[#00FF87]/40 bg-[#00FF87]/10 px-2 py-0.5 font-mono2 text-xs font-bold text-[#00FF87]">{player.signal.score}</span>
                </div>
              ) : (
                <div className="mt-3 rounded-lg border border-[#1F293D] bg-[#07090E]/60 p-3" data-testid="card-no-trigger">
                  <p className="text-xs text-slate-400">{t("cards.no_trigger")}</p>
                  <Link to="/auth?mode=register" className="mt-1 inline-block font-mono2 text-[11px] font-bold uppercase tracking-wider text-[#00FF87] hover:underline" data-testid="card-alert-signup">{t("cards.alert_signup")}</Link>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="hud-card h-fit rounded-xl p-5" data-testid="card-listings">
          <h2 className="mb-1 font-display text-xl font-bold uppercase tracking-tight text-slate-100">{t("cards.listings_title")}</h2>
          <p className="mb-4 font-mono2 text-[10px] text-slate-500">{t("cards.listings_note")}</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {links.map((m) => (
              <a
                key={m.id}
                href={m.url.replace("{QUERY}", q)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`marketplace-link-${m.id}`}
                className="flex items-center justify-between rounded-md border border-[#1F293D] px-3 py-2.5 font-mono2 text-xs font-bold uppercase tracking-wider text-slate-200 transition-colors hover:border-[#00FF87]/50 hover:text-[#00FF87]"
              >
                {t("cards.search_on")} {m.label}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
